import { useEffect, useRef } from "react";
import { finishApp, inStandaloneApp } from "./app-shell-bridge.js";
import { EXIT_TRAP, isExitTrap, isRootScreen, popIsExitPress } from "./back-target.js";
import { consumeIfOwnNavigation } from "./own-navigation.js";
import type { Route } from "./route.js";

/**
 * Wires the hardware and browser back keys to plain history.
 *
 * Back goes to whatever screen the visitor came from, exactly like any other web page — no
 * logical parent, no correction, nothing hierarchical. The one exception is Kiddora's own roots
 * (`#/play`, `#/class`, `#/`), where an installed app or the Android shell has to close rather
 * than leaving the site behind or doing nothing; `back-target.ts` decides that part, pure, so it
 * can be tested without a DOM.
 *
 * Two sources, because two shells:
 *   - The Android shell dispatches `kiddora-system-back` BEFORE any navigation happens, and
 *     deliberately does not call the WebView's own back — so a real `window.history.back()` has
 *     to be issued from here instead, exactly as if the hardware key had done it directly.
 *   - A browser or installed PWA fires `popstate` AFTER the browser has already moved. Off a root,
 *     that is the whole story: the browser went where its history said, which is genuinely where
 *     the visitor came from, and the route binding paints it. Only leaving a root needs anything
 *     further from us, and only in a standalone app.
 */
export function useBackNavigation(route: Route): void {
  // Read inside the listeners, which are registered once — a stale closure here would judge a
  // parent's back press against whatever screen they were on when the app started.
  const routeRef = useRef(route);
  useEffect(() => {
    routeRef.current = route;
  });

  /** Keeps a spare entry beneath a root screen, so back there is answerable. */
  useEffect(() => {
    if (!inStandaloneApp()) return;
    if (!isRootScreen(route)) return;
    if (isExitTrap(window.history.state)) return;
    window.history.pushState(EXIT_TRAP, "", window.location.href);
  }, [route]);

  useEffect(() => {
    /** Android: fired before anything has navigated, so the current route is still current. */
    const onSystemBack = () => {
      if (isRootScreen(routeRef.current)) {
        // Only the shell can close the app. In a plain browser tab this does nothing, which is
        // right — the page stays and the browser's own back is left alone.
        if (inStandaloneApp()) finishApp();
        return;
      }
      window.history.back();
    };

    /**
     * Browser and installed PWA: fired AFTER the browser has already moved.
     *
     * Only reached outside the Android shell. There, Android swallows the hardware key and
     * dispatches its own event instead of calling the WebView's back, so no `popstate` of ours is
     * ever produced by a back press.
     */
    const onPopState = () => {
      // See `own-navigation.ts`: a forward hash push also fires a `popstate`, on top of the
      // `hashchange` it is meant to fire alone. A real back press cannot land inside the task a
      // forward push runs in, so anything marked there is this, not a press to answer.
      if (consumeIfOwnNavigation()) return;

      if (
        !popIsExitPress({
          route: routeRef.current,
          standalone: inStandaloneApp(),
          stateAfterPop: window.history.state,
        })
      ) {
        return;
      }

      finishApp();
      /**
       * Still here, so nothing closed us — which is the normal case for an installed PWA, where
       * no script can close the window.
       *
       * The trap has to be re-armed or the NEXT press drops the parent out of the app onto
       * whatever page preceded it. Keeping them put is the right answer: an installed app that
       * silently becomes a browser tab is worse than a back key that does nothing.
       */
      if (!isExitTrap(window.history.state)) {
        window.history.pushState(EXIT_TRAP, "", window.location.href);
      }
    };

    window.addEventListener("kiddora-system-back", onSystemBack);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("kiddora-system-back", onSystemBack);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);
}
